"use client";

import { useCallback, useEffect, useRef } from "react";
import { QueryKey, useQueryClient } from "@tanstack/react-query";

const DEFAULT_COALESCE_MS = 2000;

/**
 * Returns a stable function that invalidates `queryKey` at most once per
 * `intervalMs`, collapsing a burst of calls into a single refetch.
 *
 * Bulk backend operations emit one CloudEvent per record — a CRM contact sync
 * can push thousands of `client.created` / `client.updated` events in a
 * minute. Calling `invalidateQueries` per event is pathological for a list
 * backed by an infinite query:
 *
 *  - `invalidateQueries` defaults to `cancelRefetch: true`, so each event
 *    restarts the refetch from the first page instead of joining the one
 *    already in flight — there is no natural back-pressure, and
 *    "cancelled" fetches still complete over the network because the query
 *    functions do not forward the AbortSignal to axios.
 *  - invalidating an infinite query refetches *every* loaded page, so the
 *    cost per event scales with how far the user has scrolled.
 *
 * Together that turns N events into roughly N x (pages loaded) requests.
 * Coalescing bounds it to one refetch per window regardless of event volume.
 *
 * The leading edge fires immediately so an isolated, user-visible update (one
 * client edited in the CRM) still lands right away; anything else arriving
 * inside the window collapses into a single trailing refetch.
 */
export const useCoalescedInvalidate = (
  queryKey: QueryKey,
  intervalMs: number = DEFAULT_COALESCE_MS,
) => {
  const queryClient = useQueryClient();

  // Held in a ref so callers can pass an inline `[KEY]` literal without
  // changing the returned callback's identity on every render — the callback
  // is a dependency of WebSocket subscribe effects.
  const queryKeyRef = useRef(queryKey);
  const lastInvalidatedAtRef = useRef(0);
  const trailingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    queryKeyRef.current = queryKey;
  });

  useEffect(
    () => () => {
      if (trailingTimeoutRef.current) {
        clearTimeout(trailingTimeoutRef.current);
      }
    },
    [],
  );

  return useCallback(() => {
    // A trailing refetch is already queued; this event is covered by it.
    if (trailingTimeoutRef.current) return;

    const invalidate = () => {
      trailingTimeoutRef.current = null;
      lastInvalidatedAtRef.current = Date.now();
      queryClient.invalidateQueries({ queryKey: queryKeyRef.current });
    };

    const elapsed = Date.now() - lastInvalidatedAtRef.current;

    if (elapsed >= intervalMs) {
      invalidate();
    } else {
      trailingTimeoutRef.current = setTimeout(invalidate, intervalMs - elapsed);
    }
  }, [queryClient, intervalMs]);
};
